import { inject, Injectable } from '@angular/core';
import { TrackedMessagesApiService, Visibility } from '@vendasta/email';
import { ConversationChannel } from '@vendasta/conversation';
import { ConversationMessage } from '../interface/conversation.interface';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class PersonalEmailContentService {
  private readonly trackedMessagesApi = inject(TrackedMessagesApiService);

  enrichMessages(messages: ConversationMessage[], namespace: string): Observable<ConversationMessage[]> {
    const isPersonalEmail = (msg: ConversationMessage) =>
      msg.channel === ConversationChannel.CONVERSATION_CHANNEL_PERSONAL_EMAIL;

    const messagesNeedingContent = messages.filter(
      (msg) => isPersonalEmail(msg) && !msg.body && msg.externalMessageId,
    );

    if (messagesNeedingContent.length === 0) {
      return of(messages);
    }

    const trackedMessageIds = messagesNeedingContent
      .map((msg) => msg.externalMessageId)
      .filter((id): id is string => !!id);

    return this.trackedMessagesApi.getMultiTrackedMessage({ namespace, trackedMessageIds }).pipe(
      map((response) => {
        const trackedMessagesById = new Map(
          (response.trackedMessages ?? []).map((r) => [r.trackedMessageId, r.trackedMessage]),
        );

        return messages.map((msg) => {
          if (!isPersonalEmail(msg) || msg.body || !msg.externalMessageId) {
            return msg;
          }

          const trackedMessage = trackedMessagesById.get(msg.externalMessageId);
          const content = trackedMessage?.content;

          if (!content) {
            const subjectMeta = msg.metadata?.find((m) => m.key === 'emailSubject');
            if (trackedMessage) {
              return { ...msg, emailSubject: subjectMeta?.value, emailContentHidden: true };
            }
            return subjectMeta?.value ? { ...msg, emailSubject: subjectMeta.value } : msg;
          }

          if (content.visibility === Visibility.VISIBILITY_REDACTED) {
            return { ...msg, emailSubject: content.subject, emailContentHidden: true };
          }

          return {
            ...msg,
            emailSubject: content.subject,
            body: content.htmlContent ?? content.plainTextContent ?? '',
          };
        });
      }),
      catchError((error) => {
        console.error('Failed to fetch personal email content', error);
        return of(messages);
      }),
    );
  }
}
